import { ref } from 'vue'
import axios from '@/utils/axios'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'
import { UploadableFileTypes } from '@/enums/enums'

export default function useFiles() {
  const RESOURCE = '/files'

  const { showSnackbar } = useSnackbar()

  const selectedFile = ref([])
  const uploadProgress = ref(0)
  const isLoading = ref(false)

  let abortController = null

  const getFileTypeFromFileObject = fileObject => {
    const type = fileObject?.type ?? null

    if (!type) {
      return false
    }

    if (fileObject.type.startsWith('image/')) {
      return UploadableFileTypes.IMAGE
    }

    if (fileObject.type.startsWith('video/')) {
      return UploadableFileTypes.VIDEO
    }

    return false
  }

  const sanitizeFileName = name => name
    .replace(/[^a-zA-Z0-9_.-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 255)

  const uploadFile = () => {
    const fileType = getFileTypeFromFileObject(selectedFile.value)

    if (!fileType) {
      showSnackbar(
        'Failed to upload the file: invalid file type',
        SnackbarType.ERROR,
      )

      throw new Error('Invalid file type')
    }

    isLoading.value = true

    const formData = new FormData()
    let file = selectedFile.value
    if (file && file.name) {
      const sanitized = sanitizeFileName(file.name)
      if (sanitized !== file.name) {
        file = new File([file], sanitized, { type: file.type })
      }
    }
    formData.append('file', file)

    abortController = new AbortController()

    return axios.post(`${RESOURCE}/${fileType}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal: abortController.signal,
      onUploadProgress: event => {
        uploadProgress.value = Math.round((event.loaded / event.total) * 100)
      },
    })
      .then(response => {
        showSnackbar('You have successfully uploaded the file', SnackbarType.SUCCESS)

        selectedFile.value = []

        return response.data
      })
      .catch(error => {
        const message = error.response?.data?.message ?? error.message
        const statusCode = error.response?.status ?? null

        showSnackbar(
          `Failed to upload the file:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
          SnackbarType.ERROR,
        )

        throw error
      })
      .finally(() => {
        isLoading.value = false
        uploadProgress.value = 0
      })
  }

  // eslint-disable-next-line no-unused-vars
  const deleteFile = async () => {
    // TODO: ...
  }

  const listFiles = async fileType => {
    isLoading.value = true

    return axios.get(`${RESOURCE}/${fileType}`)
      .then(response => response.data)
      .catch(error => {
        const message = error.response?.data?.message ?? error.message
        const statusCode = error.response?.status ?? null

        showSnackbar(
          `Failed to fetch list of files:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
          SnackbarType.ERROR,
        )

        throw error
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const cancelUpload = () => {
    isLoading.value = false
    uploadProgress.value = 0

    if (abortController) {
      abortController.abort()
    }
  }

  const uploadScoringFile = () => {
    const fileType = getFileTypeFromFileObject(selectedFile.value)

    if (!fileType) {
      showSnackbar(
        'Failed to upload the file: invalid file type',
        SnackbarType.ERROR,
      )

      throw new Error('Invalid file type')
    }

    isLoading.value = true

    const formData = new FormData()
    let file = selectedFile.value
    if (file && file.name) {
      const sanitized = sanitizeFileName(file.name)
      if (sanitized !== file.name) {
        file = new File([file], sanitized, { type: file.type })
      }
    }
    formData.append('file', file)

    abortController = new AbortController()

    return axios.post(`${RESOURCE}/scoring`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal: abortController.signal,
      onUploadProgress: event => {
        uploadProgress.value = Math.round((event.loaded / event.total) * 100)
      },
    })
      .then(response => {
        showSnackbar('You have successfully uploaded the scoring file', SnackbarType.SUCCESS)

        return response.data
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          showSnackbar('Upload canceled', SnackbarType.WARNING)
        } else {
          const message = error.response?.data?.message ?? error.message
          const statusCode = error.response?.status ?? null
          showSnackbar(
            `Failed to upload the scoring file:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
            SnackbarType.ERROR,
          )
        }
        throw error
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const uploadThumbFile = () => {
    const fileType = getFileTypeFromFileObject(selectedFile.value)

    if (!fileType) {
      showSnackbar('Failed to upload the file: invalid file type', SnackbarType.ERROR)
      throw new Error('Invalid file type')
    }

    isLoading.value = true
    const formData = new FormData()
    let file = selectedFile.value

    if (file && file.name) {
      const sanitized = sanitizeFileName(file.name)
      if (sanitized !== file.name) {
        file = new File([file], sanitized, { type: file.type })
      }
    }
    formData.append('file', file)
    abortController = new AbortController()

    return axios.post(`${RESOURCE}/thumb`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal: abortController.signal,
      onUploadProgress: event => {
        uploadProgress.value = Math.round((event.loaded / event.total) * 100)
      },
    })
      .then(response => {
        showSnackbar('You have successfully uploaded the thumbnail', SnackbarType.SUCCESS)

        return response.data
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          showSnackbar('Upload canceled', SnackbarType.WARNING)
        } else {
          const message = error.response?.data?.message ?? error.message
          const statusCode = error.response?.status ?? null
          showSnackbar(
            `Failed to upload the thumbnail:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
            SnackbarType.ERROR,
          )
        }
        throw error
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const uploadCompanyLogoFile = async () => {
    const fileType = getFileTypeFromFileObject(selectedFile.value)

    if (!fileType) {
      showSnackbar(
        'Failed to upload the file: invalid file type',
        SnackbarType.ERROR,
      )

      throw new Error('Invalid file type')
    }

    isLoading.value = true

    const formData = new FormData()
    let file = selectedFile.value
    if (file && file.name) {
      const sanitized = sanitizeFileName(file.name)
      if (sanitized !== file.name) {
        file = new File([file], sanitized, { type: file.type })
      }
    }
    formData.append('file', file)

    abortController = new AbortController()

    return axios.post(`${RESOURCE}/company_logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal: abortController.signal,
      onUploadProgress: event => {
        uploadProgress.value = Math.round((event.loaded / event.total) * 100)
      },
    })
      .then(response => {
        showSnackbar('You have successfully uploaded the company logo', SnackbarType.SUCCESS)

        return response.data
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          showSnackbar('Upload canceled', SnackbarType.WARNING)
        } else {
          const message = error.response?.data?.message ?? error.message
          const statusCode = error.response?.status ?? null
          showSnackbar(
            `Failed to upload the company logo:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
            SnackbarType.ERROR,
          )
        }
        throw error
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const resetState = () => {
    selectedFile.value = []
    uploadProgress.value = 0
    isLoading.value = false
  }

  return {
    selectedFile,
    uploadProgress,
    isLoading,

    cancelUpload,
    uploadFile,
    uploadScoringFile,
    uploadThumbFile,
    uploadCompanyLogoFile,
    deleteFile,
    listFiles,
    resetState,
  }
}
